"use client"

import { useState, useEffect, useRef } from "react"
import { Search, X } from "lucide-react"

interface UniversalSearchProps {
  onSearch?: (query: string) => void
}

export function UniversalSearch({ onSearch }: UniversalSearchProps) {
  const [isExpanded, setIsExpanded] = useState(false)
  const [searchQuery, setSearchQuery] = useState("")
  const searchInputRef = useRef<HTMLInputElement>(null)
  const searchContainerRef = useRef<HTMLDivElement>(null)

  // Focus input when expanded
  useEffect(() => {
    if (isExpanded && searchInputRef.current) {
      searchInputRef.current.focus()
    }
  }, [isExpanded])

  // Close search when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        searchContainerRef.current &&
        !searchContainerRef.current.contains(event.target as Node)
      ) {
        if (searchQuery.trim() === "") {
          setIsExpanded(false)
        }
      }
    }

    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [searchQuery])

  // Handle escape key
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        if (searchQuery.trim() === "") {
          setIsExpanded(false)
        } else {
          setSearchQuery("")
        }
      }
    }

    document.addEventListener("keydown", handleEscape)
    return () => document.removeEventListener("keydown", handleEscape)
  }, [searchQuery])

  const handleSearchClick = () => {
    setIsExpanded(true)
  }

  const handleCloseSearch = () => {
    setSearchQuery("")
    setIsExpanded(false)
  }

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (searchQuery.trim()) {
      console.log("Searching for:", searchQuery)
      onSearch?.(searchQuery)
    }
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value)
  }

  return (
    <div
      ref={searchContainerRef}
      className="fixed top-12 right-6 z-50" // Positioned top-right to avoid sidebar
    >
      {/* Search Container */}
      <div
        className={`
          flex items-center bg-teal-600/70 hover:bg-teal-600 border border-teal-600/70 rounded-lg shadow-lg
          transition-all duration-300 ease-in-out
          ${isExpanded ? "w-80 px-4" : "w-12 px-0"}
          hover:shadow-xl
        `}
      >
        {/* Search Icon/Button */}
        <button
          onClick={isExpanded ? handleSearchSubmit : handleSearchClick}
          className={`
            flex items-center justify-center flex-shrink-0
            transition-all duration-200
            ${isExpanded ? "w-6 h-12 mr-3" : "w-12 h-12"}
            text-white hover:text-white
          `}
          aria-label={isExpanded ? "Search" : "Open search"}
        >
          <Search className="w-6 h-6" />
        </button>

        {/* Search Input */}
        {isExpanded && (
          <form onSubmit={handleSearchSubmit} className="flex-1 flex items-center">
            <input
              ref={searchInputRef}
              type="text"
              value={searchQuery}
              onChange={handleInputChange}
              placeholder="Search across the app..."
              className="w-full h-12 bg-transparent border-0 focus:outline-none focus:ring-0 text-white placeholder-teal-100/80 text-sm"
              aria-label="Search input"
            />
            
            {/* Clear Button */}
            {searchQuery && (
              <button
                type="button"
                onClick={() => setSearchQuery("")}
                className="flex items-center justify-center w-6 h-6 text-teal-100/70 hover:text-white ml-2"
                aria-label="Clear search"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </form>
        )}

        {/* Close Button */}
        {isExpanded && (
          <button
            onClick={handleCloseSearch}
            className="flex items-center justify-center w-8 h-8 text-teal-100/70 hover:text-white transition-colors ml-2"
            aria-label="Close search"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Search Results Dropdown (Optional) */}
      {isExpanded && searchQuery && (
        <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-gray-200 rounded-lg shadow-xl max-h-96 overflow-y-auto z-50">
          {/* Sample search results - Replace with actual search logic */}
          <div className="p-4">
            <div className="text-sm text-gray-500 mb-2">Quick results for "{searchQuery}"</div>
            
            {/* Sample Results */}
            <div className="space-y-2">
              <button className="w-full text-left p-2 hover:bg-gray-50 rounded text-sm text-gray-700">
                    <span className="font-medium">Learning Resources</span> - {searchQuery}
              </button>
              <button className="w-full text-left p-2 hover:bg-gray-50 rounded text-sm text-gray-700">
                <span className="font-medium">Career Goals</span> related to {searchQuery}
              </button>
              <button className="w-full text-left p-2 hover:bg-gray-50 rounded text-sm text-gray-700">
                <span className="font-medium">Assessment</span> - {searchQuery} Quiz
              </button>
              <button className="w-full text-left p-2 hover:bg-gray-50 rounded text-sm text-gray-700">
                <span className="font-medium">Career Path</span> - {searchQuery} Specialist
              </button>
            </div>

            {/* View All Results */}
            <button className="w-full text-center p-3 border-t border-gray-100 text-teal-600 hover:text-teal-700 text-sm font-medium mt-2">
              View all results for "{searchQuery}"
            </button>
          </div>
        </div>
      )}
    </div>
  )
}